/**
 * Pure Playbook helpers — prompt assembly + section grouping. Free of React/IO
 * so they are directly unit-testable.
 */
import type { PlaybookTemplate, PlaybookData } from "./types";

/** Matches a `{{key}}` placeholder, capturing the surrounding spaces too. */
const SLOT_RE = /\s*\{\{\s*([\w一-鿿-]+)\s*\}\}\s*/g;

/**
 * Build the final prompt from a template + user-entered slot values.
 *
 * Non-blocking by design: a blank slot falls back to its `default`, so the
 * assembled prompt is always complete and natural even if the user fills
 * nothing. Only a slot with neither value nor default drops its fragment. A
 * literal `{{key}}` is never emitted.
 */
export function assemblePrompt(t: PlaybookTemplate, values: Record<string, string> = {}): string {
  const byKey = new Map((t.slots ?? []).map((s) => [s.key, s]));
  const out = t.promptTemplate.replace(SLOT_RE, (_m, key: string) => {
    const entered = values[key]?.trim();
    if (entered) return ` ${entered} `;
    const def = byKey.get(key)?.default?.trim();
    if (def) return ` ${def} `;
    return " ";
  });
  return out.replace(/\s{2,}/g, " ").trim();
}

/** A section = a display group (用小环 / a role) with its template cards. */
export interface PlaybookSection {
  section: string;
  cases: PlaybookTemplate[];
}

/**
 * Drop cases gated on a runtime feature flag that is off. A case with no `flag`
 * always shows; a flagged case shows only when `features[flag] === true`. This is
 * the single source of truth for flag visibility, so a release committed
 * flag-off (e.g. the R1 `structuredAnswer` cards) cannot leak into the gallery
 * until the prod flag flips. Pure, so the no-leak guarantee is unit-testable.
 */
export function gateCases<T extends { flag?: string }>(
  cases: T[],
  features?: Record<string, boolean>
): T[] {
  return cases.filter((c) => c.flag === undefined || features?.[c.flag] === true);
}

/** Days a card shows a「NEW」badge after its `since` (prod go-live) date. */
export const PLAYBOOK_NEW_WINDOW_DAYS = 7;

/**
 * True when a card is still inside its「NEW」window: `now - since < window`.
 * `since` is an ISO `YYYY-MM-DD`; `now` is injected for testability. A missing or
 * unparseable `since` is never new. Pure, so the window logic is unit-testable and
 * the badge auto-expires with no cleanup.
 */
export function isNewCard(since: string | undefined, now: number = Date.now()): boolean {
  if (since === undefined) return false;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return false;
  return now - t < PLAYBOOK_NEW_WINDOW_DAYS * 86_400_000;
}

/**
 * Group templates into sections in the dataset's `sections` order. Empty
 * sections are omitted; any section not listed is appended last (defensive).
 */
export function groupBySection(data: PlaybookData): PlaybookSection[] {
  const order = data.sections ?? [];
  const seen = new Set<string>();
  const groups: PlaybookSection[] = [];
  const push = (name: string): void => {
    if (seen.has(name)) return;
    seen.add(name);
    const inSec = data.cases.filter((c) => c.section === name);
    if (inSec.length > 0) groups.push({ section: name, cases: inSec });
  };
  for (const name of order) push(name);
  for (const c of data.cases) push(c.section);
  return groups;
}
